import { Button, ButtonRow, Icon, Text } from '@timelyapp/react';
import { Close } from 'components/Icons';
import InlineMessage from 'components/InlineMessage';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getTicViewDemoMode } from 'redux/modules/tic/tic.demo.selectors';
import { saveMemoryConfig } from 'redux/modules/tic/tic.memories.actions';
import type { ThunkDispatch } from 'types/redux';
import styles from './DemoModeBanner.module.scss';

interface Props {
  message: React.ReactNode;
}

/**
 * The demo on/off toggle lives on the viewer's own account (TIC memory_filters,
 * keyed by person), not on the workspace being viewed — so it's possible to land
 * on a real, unrelated workspace with the preset still on from an earlier
 * session, and the generated roster is deliberately believable enough that
 * nothing else on the page gives it away. Shown from the People routes only.
 */
export default function DemoModeBanner({ message }: Props) {
  const dispatch = useDispatch<ThunkDispatch>();
  const ticViewDemoMode = useSelector(getTicViewDemoMode);
  // Not persisted: dismissing hides this instance of the banner, not future
  // ones — if the preset is still on, it's still worth surfacing next time.
  const [isDismissed, setIsDismissed] = useState(false);

  const handleExitDemoMode = useCallback(() => {
    dispatch(saveMemoryConfig({ demoSettings: { ...ticViewDemoMode, enabled: false } }));
  }, [dispatch, ticViewDemoMode]);

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  if (isDismissed) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <InlineMessage type="warning" noMargin>
        <div className={styles.row}>
          <div className={styles.content}>
            <Icon name="AlertTriangle" />
            <Text>{message}</Text>
          </div>
          <ButtonRow className={styles.actions}>
            <Button size="small" onClick={handleExitDemoMode}>
              Exit demo mode
            </Button>
            <Button priority="low" size="small" onClick={handleDismiss} aria-label="Dismiss">
              <Close width={10} height={10} />
            </Button>
          </ButtonRow>
        </div>
      </InlineMessage>
    </div>
  );
}
